import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@feathr/components';

/** How long the copy button shows its copied-confirmation state, in ms. */
export const COPIED_FEEDBACK_DURATION = 5000;

export interface IUseAPIKeySecretModalProps {
  /** Duration of the transient copied-confirmation state, in ms. */
  copiedFeedbackDuration?: number;
  /**
   * Called when the modal is allowed to close: Done, a close attempt after copying, or a
   * confirmed close-without-copying. The parent must discard the secret when this fires —
   * it can never be displayed again.
   */
  onClose: () => void;
  opened: boolean;
  /** The one-time plaintext key value to copy. */
  secret: string;
}

export interface IUseAPIKeySecretModalResult {
  handleCloseAttempt: () => void;
  handleCloseWarningBack: () => void;
  handleCloseWarningConfirm: () => void;
  handleCopy: () => Promise<void>;
  hasCopied: boolean;
  isCloseWarningOpen: boolean;
  showCopiedFeedback: boolean;
}

/**
 * State for the one-time API key secret display: whether the user has copied the key
 * (gates the Done button), the transient copied-confirmation state on the copy button,
 * and the warning shown when the user attempts to close before copying.
 */
export function useAPIKeySecretModal({
  copiedFeedbackDuration = COPIED_FEEDBACK_DURATION,
  onClose,
  opened,
  secret,
}: IUseAPIKeySecretModalProps): IUseAPIKeySecretModalResult {
  const { t } = useTranslation();
  const [hasCopied, setHasCopied] = useState(false);
  const [showCopiedFeedback, setShowCopiedFeedback] = useState(false);
  const [isCloseWarningOpen, setIsCloseWarningOpen] = useState(false);
  const feedbackTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => clearTimeout(feedbackTimeout.current);
  }, []);

  /*
   * Reset when the modal closes. A parent that stays mounted and toggles `opened` would
   * otherwise reopen for a NEW key with the previous key's copied state, skipping both the
   * Done gate and the close-before-copy warning.
   */
  useEffect(() => {
    if (!opened) {
      setHasCopied(false);
      setShowCopiedFeedback(false);
      setIsCloseWarningOpen(false);
      clearTimeout(feedbackTimeout.current);
    }
  }, [opened]);

  async function handleCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(secret);
    } catch {
      toast(t("Couldn't copy the key to your clipboard. Try again."), { type: 'error' });
      return;
    }
    setHasCopied(true);
    setShowCopiedFeedback(true);
    clearTimeout(feedbackTimeout.current);
    feedbackTimeout.current = setTimeout(
      () => setShowCopiedFeedback(false),
      copiedFeedbackDuration,
    );
  }

  function handleCloseAttempt(): void {
    if (hasCopied) {
      onClose();
      return;
    }
    setIsCloseWarningOpen(true);
  }

  function handleCloseWarningBack(): void {
    setIsCloseWarningOpen(false);
  }

  function handleCloseWarningConfirm(): void {
    setIsCloseWarningOpen(false);
    onClose();
  }

  return {
    handleCloseAttempt,
    handleCloseWarningBack,
    handleCloseWarningConfirm,
    handleCopy,
    hasCopied,
    isCloseWarningOpen,
    showCopiedFeedback,
  };
}
